import { useMemo } from 'react';
import { buildAadSignInUrl, buildBackToInvitationUrl, buildFacebookSignInUrl, buildGoogleSignInUrl, buildMsaSignInUrl, buildSwitchEmailUrl } from './query-params';
import { clearPersistedFormDraft } from './components/activation-persistence';
import type { AcceptInvitationTelemetry } from './use-accept-invitation-telemetry';

export type InvitationScreenActionsInput = {
  telemetry: AcceptInvitationTelemetry;
  code: string;
  // ActivationOptions `expectedUsername` — used as the federated sign-in login hint.
  expectedUsername?: string | null;
  // ActivationOptions `expectedIdentityType` — picks the provider for the alternate-account continue.
  expectedIdentityType?: string | null;
  // Validate `customerId` — the account the already-has-access page sends the user into.
  customerId?: number | string | null;
  onUseExistingProfileConfirmed: () => void;
};

export type InvitationScreenActions = {
  handleUseDifferentEmail: () => void;
  handleGoToAccount: () => void;
  handleUseExistingProfile: () => void;
  handleContinueWithWorkAccount: () => void;
  handleContinueWithPersonalAccount: () => void;
  handleSignInWithGoogle: () => void;
  handleSignInWithFacebook: () => void;
  handleContinueAsAlternateAccount: () => void;
  handleBackToInvitation: () => void;
  handleProfileFormSignOut: () => void;
};

const signInUrlBuildersByIdentityType: Record<string, (code: string, win: Window, loginHint?: string | null) => string> = {
  AAD: buildAadSignInUrl,
  MSA: buildMsaSignInUrl,
  Google: buildGoogleSignInUrl,
  Facebook: buildFacebookSignInUrl,
};

// Every button on every post-auth error page / dialog, plus the profile form's sign-out. Each one
// emits its telemetry BEFORE navigating: the trace is handed synchronously to the instrumentation
// queue, so it is already dispatched by the time the unload starts. `logScreenAction` stamps the
// screen from the resolved post-auth outcome, so a handler shared by several screens (e.g. "Use a
// different email") stays attributable without a per-call-site wrapper.
export const useInvitationScreenActions = ({
  telemetry, code, expectedUsername, expectedIdentityType, customerId, onUseExistingProfileConfirmed,
}: InvitationScreenActionsInput): InvitationScreenActions => useMemo(() => {
  const assign = (url: string) => {
    if (url) {
      window.location.assign(url);
    }
  };

  const navigateBackToInvitation = () => assign(buildBackToInvitationUrl(code));

  return {
    handleUseDifferentEmail: () => {
      telemetry.logScreenAction('UseDifferentEmail');
      // Eagerly remove personal draft data before leaving, in addition to the identity restore guard.
      clearPersistedFormDraft(code);
      assign(buildSwitchEmailUrl(code));
    },

    // "Go to account" on the already-has-access error page — the invited email already has access, so
    // send the user into Microsoft Advertising for that account. Mirrors the confirmation page's "Go
    // to Microsoft Advertising"; there's no Activate `redirectUrl` here, so we build the same
    // `/login/live?cid=…` shape from the invited customer id (Validate `customerId`), else the default.
    handleGoToAccount: () => {
      telemetry.logScreenAction('GoToAccount');
      window.location.assign(customerId ? `/login/live?cid=${customerId}` : '/login/live');
    },

    handleUseExistingProfile: () => {
      telemetry.logScreenAction('UseExistingProfile');
      onUseExistingProfileConfirmed();
    },

    // "Continue with Microsoft" on the work-account (and AAD-only) error page — sign the current
    // MSA/Google session out and go straight to AAD sign-in.
    handleContinueWithWorkAccount: () => {
      telemetry.logScreenAction('ContinueWithWorkAccount');
      assign(buildAadSignInUrl(code, window, expectedUsername));
    },

    // "Continue with Microsoft" on the personal-account error page — sign out and go straight to MSA
    // (personal account) sign-in.
    handleContinueWithPersonalAccount: () => {
      telemetry.logScreenAction('ContinueWithPersonalAccount');
      assign(buildMsaSignInUrl(code, window, expectedUsername));
    },

    handleSignInWithGoogle: () => {
      telemetry.logScreenAction('SignInWithGoogle');
      assign(buildGoogleSignInUrl(code, window, expectedUsername));
    },

    handleSignInWithFacebook: () => {
      telemetry.logScreenAction('SignInWithFacebook');
      assign(buildFacebookSignInUrl(code, window, expectedUsername));
    },

    // "Continue" on the same-provider alternate-account page (Case 1) — sign the current session out
    // and re-authenticate as the matched account (`expectedUsername`) of the SAME provider so the user
    // can accept the invitation with it. No-ops if the provider is unknown.
    handleContinueAsAlternateAccount: () => {
      telemetry.logScreenAction('ContinueAsAlternateAccount');
      const build = signInUrlBuildersByIdentityType[expectedIdentityType || ''];
      assign(build ? build(code, window, expectedUsername) : '');
    },

    // "Go back" / "Back to invitation" — sign out and return to the invitation choice view.
    handleBackToInvitation: () => {
      telemetry.logScreenAction('BackToInvitation');
      navigateBackToInvitation();
    },

    // Top-bar "Sign out" on the profile form. Navigates directly so this exit is reported only as
    // `SignedOut`, not also as a "Back to invitation" error-screen action.
    handleProfileFormSignOut: () => {
      telemetry.logSignOut('ProfileForm');
      // See handleUseDifferentEmail — signing out must not leave this identity's data for the next one.
      clearPersistedFormDraft(code);
      navigateBackToInvitation();
    },
  };
}, [telemetry, code, expectedUsername, expectedIdentityType, customerId, onUseExistingProfileConfirmed]);
